import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { IMaskInput } from 'react-imask';
import { useTheme } from '../../context/ThemeContext';
import Button from '../UI/Button';
import { StepProps } from '../../types';

interface Step1Data {
  firm_name: string;
  cac_number: string;
  address: string;
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const Title = styled.h2<{ theme: any }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const Description = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const TextArea = styled.textarea.withConfig({
  shouldForwardProp: (prop) => !['theme', 'hasError'].includes(prop),
})<{ theme: any; hasError: boolean }>`
  padding: 0.75rem;
  border: 2px solid ${props => props.hasError ? props.theme.colors.error : props.theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Raleway', sans-serif;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? props.theme.colors.error : props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.hasError ? props.theme.colors.error : props.theme.colors.primary}33;
  }

  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label<{ theme: any }>`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-size: 0.875rem;
`;

const ErrorMessage = styled.span<{ theme: any }>`
  color: ${props => props.theme.colors.error};
  font-size: 0.75rem;
  font-weight: 500;
`;

const InfoMessage = styled.span<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.75rem;
  font-style: italic;
  opacity: 0.8;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Step1FirmInfo: React.FC<StepProps> = ({ data, onNext }) => {
  const { theme } = useTheme();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<Step1Data>({
    defaultValues: {
      firm_name: data.firm_name || '',
      cac_number: data.cac_number || '',
      address: data.address || '',
    },
  });

  const onSubmit = (formData: Step1Data) => {
    onNext(formData);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <HeaderContainer>
        <Title theme={theme}>Firm Information</Title>
        <Description theme={theme}>
          Please provide your firm's basic information. This information will be used to verify your business registration.
        </Description>
      </HeaderContainer>

      <InputGroup>
        <Label theme={theme}>
          Firm Name <span style={{ color: theme.colors.error }}>*</span>
        </Label>
        <input
          type="text"
          placeholder="Enter your firm's registered name"
          style={{
            padding: '0.75rem',
            border: `2px solid ${errors.firm_name ? theme.colors.error : theme.colors.border}`,
            borderRadius: '8px',
            fontSize: '1rem',
            fontFamily: 'Raleway, sans-serif',
            backgroundColor: theme.colors.background,
            color: theme.colors.text,
            transition: 'all 0.3s ease',
          }}
          {...register('firm_name', {
            required: 'Firm name is required',
            minLength: {
              value: 2,
              message: 'Firm name must be at least 2 characters',
            },
          })}
        />
        {errors.firm_name && (
          <ErrorMessage theme={theme}>{errors.firm_name.message}</ErrorMessage>
        )}
      </InputGroup>

      <InputGroup>
        <Label theme={theme}>
          CAC Number <span style={{ color: theme.colors.error }}>*</span>
        </Label>
        <Controller
          name="cac_number"
          control={control}
          rules={{
            required: 'CAC number is required',
            validate: (value) => {
              const cleanValue = value?.toUpperCase().trim() || '';
              if (cleanValue.length !== 9) {
                return 'CAC number must be exactly 9 characters (2 letters + 7 digits)';
              }
              if (!/^[A-Z]{2}\d{7}$/.test(cleanValue)) {
                return 'CAC number must be 2 letters followed by 7 digits (e.g., RC1234567)';
              }
              return true;
            },
          }}
          render={({ field }) => (
            <IMaskInput
              mask="aa0000000"
              definitions={{
                'a': /[A-Za-z]/,
                '0': /[0-9]/
              }}
              placeholder="RC1234567"
              lazy={false}
              value={field.value}
              onAccept={(value) => {
                field.onChange(value.toUpperCase());
              }}
              style={{
                padding: '0.75rem',
                border: `2px solid ${errors.cac_number ? theme.colors.error : theme.colors.border}`,
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Raleway, sans-serif',
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                transition: 'all 0.3s ease',
              }}
            />
          )}
        />
        <InfoMessage theme={theme}>Format: 2 letters + 7 digits (e.g., RC1234567)</InfoMessage>
        {errors.cac_number && (
          <ErrorMessage theme={theme}>{errors.cac_number.message}</ErrorMessage>
        )}
      </InputGroup>

      {/* Tax ID field removed per updated flow */}

      <InputGroup>
        <Label theme={theme}>
          Business Address <span style={{ color: theme.colors.error }}>*</span>
        </Label>
        <TextArea
          theme={theme}
          placeholder="Enter your firm's complete business address"
          hasError={!!errors.address}
          {...register('address', {
            required: 'Business address is required',
            minLength: {
              value: 10,
              message: 'Please provide a complete address',
            },
          })}
        />
        {errors.address && (
          <ErrorMessage theme={theme}>{errors.address.message}</ErrorMessage>
        )}
      </InputGroup>

      <ButtonContainer>
        <Button type="submit" size="large">
          Next Step
        </Button>
      </ButtonContainer>
    </Form>
  );
};

export default Step1FirmInfo;
